import useModals from 'src/Hooks/useModals'
import { installationInvitationsApi, invitationsApi } from 'src/Libs/installation-rights-management/api'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { UseQueryResult } from '@tanstack/react-query'
import { usePortalMutation, usePortalQuery } from 'src/Libs/api'
import { InstallationInvitationModel } from 'src/Libs/installation-rights-management/models'
import { InstallationInviationDetailsModel } from 'src/Libs/installation-rights-management/models/installation-invitation.model'

interface UseInstallationAccessHook {
  /**
   * Opens a modal dialog to initiate and manage the invitation process of an User to an Installation
   * @param installationId
   */
  inviteSomeoneDialog: (installationId: number, isInvitingOwner?: boolean) => Promise<void>

  /**
   * Starts the process to remove an existing User-Invitation to an Installation
   * @param token
   */
  removeInvitation: (token: string, confirmationTitle?: string, confirmationText?: string, useModal?: boolean) => Promise<void>

  /**
   * Directly accepts an invitation without opening a modal
   * @param token
   */
  acceptInvitation: (token: string) => Promise<void>

  /**
   * Directly declines an invitation without opening a confirmation modal
   * @param token
   */
  declineInvitation: (token: string) => Promise<void>
  isAcceptLoading: boolean
  isDeclineLoading: boolean

  getInvitationByToken: (token: string | undefined) => UseQueryResult<InstallationInviationDetailsModel | null, Error>

  /**
   * Resends an existing User-Invitation to an Installation
   * @param installationId
   * @param token
   */
  resendInvitation: (installationId: number, token: string) => Promise<void>
  /**
   * invite function without modal
   */
  inviteUser: (installationId: number, email: string, isInvitingOwner?: boolean, isPlanned?: boolean) => Promise<void>
  /**
   * Get all existing invitations for installation
   */
  getInvitations: (installationId?: number, enabled?: boolean) => UseQueryResult<InstallationInvitationModel[] | null, Error>
  /**
   * Indicates whether the removal of an invitation is currently in progress
   */
  isRemoveLoading: boolean
}

// Exposed as method from useInstallationInvitation: callers pass installationId and receive useQuery result.
const useInstallationInvitationsQuery = (installationId?: number, enabled = true): UseQueryResult<InstallationInvitationModel[] | null, Error> => {
  const authFetch = useAuthFetchWithResponse()

  return usePortalQuery<InstallationInvitationModel[]>({
    queryKey: ['installation-invitations', installationId],
    enabled: Boolean(installationId) && enabled,
    queryFn: () => installationInvitationsApi.findAll(installationId!, authFetch),
  })
}

export const useInstallationInvitationDetailQuery = (token: string | undefined): UseQueryResult<InstallationInviationDetailsModel | null, Error> => {
  const authFetch = useAuthFetchWithResponse()

  return usePortalQuery({
    queryKey: ['installation-invitation', token],
    queryFn: () => installationInvitationsApi.getById(authFetch, token),
    enabled: Boolean(token),
    staleTime: 0,
  })
}

/**
 * This hook provides utils to support the ui components in creating and changing an Invitation to an Installation
 *
 * It combines the workflows of ui/ux behaviours, with the direct api data access to create, read and remove an invitation.
 *
 */
const useInstallationInvitation = (): UseInstallationAccessHook => {
  const authFetch = useAuthFetchWithResponse()
  const { openInviteNewAccessToInstallation, openConfirmationModal } = useModals()
  const { t } = useCustomTranslation()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()

  const inviteMutation = usePortalMutation({
    mutationFn: ({
      installationId,
      email,
      isInvitingOwner,
      isPlanned,
    }: {
      installationId: number
      email: string
      isInvitingOwner?: boolean
      isPlanned?: boolean
    }) =>
      installationInvitationsApi.requestInvite(
        authFetch,
        installationId,
        email,
        isInvitingOwner ? 'handover' : isPlanned ? 'handover-planned' : 'invite'
      ),
    onSuccess: () => {
      enqueueSuccessStack(t('installation.access-and-invitations.invite.invite-success'))
    },
    onError: () => {
      enqueueErrorStack(t('installation.access-and-invitations.invite.invite-failed'))
    },
  })

  const acceptMutation = usePortalMutation({
    mutationFn: (token: string) => invitationsApi.acceptInvitationToken(authFetch, token),
    onError: () => {
      enqueueErrorStack(t('installation.invitation.error'))
    },
  })

  const removeMutation = usePortalMutation({
    mutationFn: (token: string) => invitationsApi.removeInvitation(authFetch, token),
    onSuccess: () => {
      enqueueSuccessStack(t('installation.access-and-invitations.remove-invite.confirm.deletion'))
    },
    onError: () => {
      enqueueErrorStack(t('installation.access-and-invitations.remove-invite.confirm.deletion-error'))
    },
  })

  const declineMutation = usePortalMutation({
    mutationFn: (token: string) => invitationsApi.removeInvitation(authFetch, token),
    onSuccess: () => {
      enqueueSuccessStack(t('installation.invitation.decline-success'))
    },
    onError: () => {
      enqueueErrorStack(t('installation.invitation.decline-error'))
    },
  })

  const resendMutation = usePortalMutation({
    mutationFn: ({ installationId, token }: { installationId: number; token: string }) =>
      invitationsApi.resendInvitation(authFetch, token, installationId.toString()),
    onSuccess: () => {
      enqueueSuccessStack(t('installation.access-and-invitations.resend.success'))
    },
    onError: () => {
      enqueueErrorStack(t('installation.access-and-invitations.resend.failed'))
    },
  })

  const inviteSomeoneDialog = async (installationId: number, isInvitingOwner?: boolean): Promise<void> => {
    return new Promise(resolve => {
      const onConfirm = (email: string, onClose: () => void): void => {
        inviteMutation.mutateAsync({ installationId, email, isInvitingOwner }).finally(() => {
          onClose()
          resolve()
        })
      }

      openInviteNewAccessToInstallation({ onConfirm, isInvitingOwner })
    })
  }

  const inviteUser = async (installationId: number, email: string, isInvitingOwner?: boolean, isPlanned?: boolean): Promise<void> => {
    await inviteMutation.mutateAsync({ installationId, email, isInvitingOwner, isPlanned })
  }

  const removeInvitation = async (token: string, confirmationTitle?: string, confirmationText?: string, useModal = true): Promise<void> => {
    return new Promise(resolve => {
      const onConfirm = (close?: () => void): void => {
        removeMutation.mutateAsync(token).finally(() => {
          close?.()
          resolve()
        })
      }

      if (useModal) {
        openConfirmationModal({
          onConfirm,
          onCancel: close => {
            close()
            resolve()
          },
          buttonText: [t('common.cancel'), t('common.invite-confirm')],
          title: confirmationTitle ?? t('installation.access-and-invitations.remove-invite.confirm.title'),
          text: confirmationText ?? t('installation.access-and-invitations.remove-invite.confirm.description'),
        })
      } else {
        onConfirm()
      }
    })
  }

  const acceptInvitation = async (token: string): Promise<void> => {
    await acceptMutation.mutateAsync(token)
  }

  const declineInvitation = async (token: string): Promise<void> => {
    await declineMutation.mutateAsync(token)
  }

  const resendInvitation = async (installationId: number, token: string): Promise<void> => {
    await resendMutation.mutateAsync({ installationId, token })
  }

  return {
    inviteSomeoneDialog,
    removeInvitation,
    acceptInvitation,
    declineInvitation,
    isAcceptLoading: acceptMutation.isPending,
    isDeclineLoading: declineMutation.isPending,
    resendInvitation,
    inviteUser,
    getInvitations: useInstallationInvitationsQuery,
    getInvitationByToken: useInstallationInvitationDetailQuery,
    isRemoveLoading: removeMutation.isPending,
  }
}

export default useInstallationInvitation
